"use client";

const FB_PIXEL_ID = process.env.NEXT_PUBLIC_FB_PIXEL_ID;

interface ReactPixelInstance {
  init: (
    pixelId: string,
    advancedMatching?: unknown,
    options?: { autoConfig: boolean; debug: boolean },
  ) => void;
  pageView: () => void;
  track: (title: string, data?: unknown) => void;
}

declare global {
  interface Window {
    fbq?: (
      action: string,
      eventName: string,
      params?: unknown,
      options?: { eventID?: string },
    ) => void;
  }
}

let initPromise: Promise<ReactPixelInstance | null> | null = null;

export function initFacebookPixel(): Promise<ReactPixelInstance | null> {
  if (typeof window === "undefined" || !FB_PIXEL_ID)
    return Promise.resolve(null);

  if (!initPromise) {
    initPromise = import("react-facebook-pixel")
      .then((module) => {
        const pixel = module.default as unknown as ReactPixelInstance;
        pixel.init(FB_PIXEL_ID, undefined, {
          autoConfig: true,
          debug: process.env.NODE_ENV === "development",
        });
        if (process.env.NODE_ENV === "development") {
          console.log("[FB Pixel] Initialized with ID:", FB_PIXEL_ID);
        }
        return pixel;
      })
      .catch((error) => {
        console.error("[FB Pixel] Failed to initialize:", error);
        return null;
      });
  }

  return initPromise;
}

async function trackEvent(
  eventName: string,
  params?: unknown,
  eventId?: string,
) {
  try {
    const pixel = await initFacebookPixel();
    if (!pixel) return;

    if (eventId && window.fbq) {
      window.fbq("track", eventName, params, { eventID: eventId });
    } else {
      pixel.track(eventName, params);
    }

    if (process.env.NODE_ENV === "development") {
      console.log(`[FB Pixel] ${eventName} tracked:`, { params, eventId });
    }
  } catch (error) {
    console.error(`[FB Pixel] Failed to track ${eventName}:`, error);
  }
}

export function isPixelPageViewPath(pathname: string): boolean {
  return (
    pathname === "/" ||
    pathname === "/pricing" ||
    pathname === "/plataforma" ||
    pathname === "/seleccionar-plan" ||
    pathname === "/landing" ||
    pathname.startsWith("/landing/") ||
    pathname === "/ofertas" ||
    pathname.startsWith("/ofertas/") ||
    pathname === "/blog" ||
    pathname.startsWith("/blog/") ||
    pathname === "/masterclass/series-y-repeticiones" ||
    pathname.startsWith("/masterclass/series-y-repeticiones/") ||
    pathname === "/clase" ||
    pathname.startsWith("/clase/") ||
    pathname === "/opciones" ||
    pathname.startsWith("/opciones/") ||
    pathname === "/onboarding" ||
    pathname === "/abdomen-plano-compra-completada" ||
    pathname === "/registro" ||
    pathname === "/login"
  );
}

export async function trackPageView(): Promise<void> {
  try {
    const pixel = await initFacebookPixel();
    if (!pixel) return;
    pixel.pageView();
  } catch (error) {
    console.error("[FB Pixel] Failed to track PageView:", error);
  }
}

export interface ViewContentParams {
  content_name?: string;
  content_category?: string;
  content_ids?: string[];
  content_type?: string;
  value?: number;
  currency?: string;
}

export function trackViewContent(
  params?: ViewContentParams,
  eventId?: string,
): Promise<void> {
  return trackEvent("ViewContent", params, eventId);
}

export interface InitiateCheckoutParams {
  value?: number;
  currency?: string;
  content_ids?: string[];
  content_type?: string;
  content_name?: string;
  num_items?: number;
}

export function trackInitiateCheckout(
  params?: InitiateCheckoutParams,
  eventId?: string,
): Promise<void> {
  return trackEvent("InitiateCheckout", params, eventId);
}

export interface PurchaseParams {
  value: number;
  currency: string;
  content_ids?: string[];
  content_type?: string;
  content_name?: string;
  num_items?: number;
}

export function trackPurchase(
  params: PurchaseParams,
  eventId?: string,
): Promise<void> {
  return trackEvent("Purchase", params, eventId);
}

export function trackCompleteRegistration(
  params?: Record<string, unknown>,
  eventId?: string,
): Promise<void> {
  return trackEvent("CompleteRegistration", params, eventId);
}

export function trackLead(
  params?: Record<string, unknown>,
  eventId?: string,
): Promise<void> {
  return trackEvent("Lead", params, eventId);
}
